import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import serviceUtil from 'src/Utils/serviceUtil/serviceUtil';

// Define the types for the context value
interface GlobalContextType {
  isNavigationBlocked: () => string | null;
  blockNavigation: () => void;
  unblockNavigation: () => void;
  userData: {
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    department: string;
    company: string;
    user_id: string;
    profilePicture: string;
  };
  setRefreshUserData: React.Dispatch<React.SetStateAction<boolean>>;
  isUnsavedChangesModalVisible: boolean;
  setIsUnsavedChangesModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  newUrl: string;
  setNewUrl: React.Dispatch<React.SetStateAction<string>>;
  onModalConfirm: (() => void) | null;
  setOnModalConfirm: React.Dispatch<React.SetStateAction<(() => void) | null>>;
  onModalCancel: (() => void) | null;
  setOnModalCancel: React.Dispatch<React.SetStateAction<(() => void) | null>>;
}

// Create the context with an initial default value
const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

// Define the type for the GlobalProvider props
interface GlobalProviderProps {
  children: ReactNode;
}

export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    department: '',
    company: '',
    user_id: '',
    profilePicture: '',
  });

  const [refreshUserData, setRefreshUserData] = useState(false);

  const [isUnsavedChangesModalVisible, setIsUnsavedChangesModalVisible] = useState<boolean>(false);

  const [newUrl, setNewUrl] = useState<string>('');

  const [onModalConfirm, setOnModalConfirm] = useState<(() => void) | null>(null);

  const [onModalCancel, setOnModalCancel] = useState<(() => void) | null>(null);

  const isNavigationBlocked = () => localStorage.getItem('blockNavigation');

  const blockNavigation = () => {
    localStorage.setItem('blockNavigation', 'true');
  };

  const unblockNavigation = () => {
    localStorage.removeItem('blockNavigation');
  };

  const getUserData = async () => {
    const accessToken = typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : '';
    if (accessToken) {
      const [, payload] = accessToken.split('.');
      const userID = JSON.parse(atob(payload)).userID;
      const response = await serviceUtil.get(`${process.env.NEXT_PUBLIC_API_URL}/identity/user/profile/${userID}`, {});
      if (response?.status === 200) {
        setUserData({ ...response.data, user_id: userID });
      }
    }
  };

  useEffect(() => {
    getUserData();
  }, []);

  useEffect(() => {
    if (refreshUserData) {
      getUserData();
      setRefreshUserData(false);
    }
  }, [refreshUserData]);

  return (
    <GlobalContext.Provider
      value={{
        isNavigationBlocked,
        blockNavigation,
        unblockNavigation,
        userData,
        setRefreshUserData,
        isUnsavedChangesModalVisible,
        setIsUnsavedChangesModalVisible,
        newUrl,
        setNewUrl,
        onModalConfirm,
        setOnModalConfirm,
        onModalCancel,
        setOnModalCancel,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

// Create a custom hook to use the context
export const useGlobalContext = (): GlobalContextType => {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobalContext must be used within a GlobalProvider');
  }
  return context;
};
